import { colors } from "../theme";
import { IdleGroveLogo } from "./IdleGroveLogo";
import type { ThemeMode } from "../themeState";

interface NavProps {
  isDark: boolean;
  scrollY: number;
  onOpenWaitlist: () => void;
  themeMode: ThemeMode;
  onCycleTheme: () => void;
}

export function Nav({ isDark, scrollY, onOpenWaitlist, themeMode, onCycleTheme }: NavProps) {
  const isLocked = themeMode !== "auto";
  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-[2000ms]"
      style={{
        backgroundColor: isDark ? `${colors.base03}E6` : `${colors.base3}E6`,
        backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${isDark ? colors.base02 : colors.base2}`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 py-3 md:px-8 md:py-6 flex items-center justify-between">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="hidden md:block">
            <IdleGroveLogo size={36} isDark={isDark} />
          </div>
          <div className="md:hidden">
            <IdleGroveLogo size={24} isDark={isDark} />
          </div>
          <span className="text-base md:text-xl font-semibold tracking-tight">
            Idle Grove
          </span>
        </div>

        <div
          className="hidden md:flex items-center gap-8 text-sm"
          style={{ color: isDark ? colors.base01 : colors.base1 }}
        >
          <a href="#efficiency" className="hover:opacity-70 transition-opacity">
            Pricing
          </a>
          <a href="#technology" className="hover:opacity-70 transition-opacity">
            How it works
          </a>
          <a href="#features" className="hover:opacity-70 transition-opacity">
            Features
          </a>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          {/* Light/Dark mode toggle */}
          <button
            onClick={onCycleTheme}
            className="flex items-center gap-1.5 md:gap-2 px-2 py-1.5 md:px-3 md:py-2 rounded-full text-xs font-medium transition-all duration-300"
            style={{
              backgroundColor: isDark ? colors.base02 : colors.base2,
              color: isDark ? colors.base01 : colors.base1,
              border: isLocked ? `1px solid ${isDark ? colors.cyan : colors.blue}40` : "1px solid transparent",
            }}
            title={
              themeMode === "auto"
                ? "Auto mode - click for light"
                : themeMode === "light"
                  ? "Light mode - click for dark"
                  : "Dark mode - click for auto"
            }
          >
            <span
              className="w-4 h-4 flex items-center justify-center transition-colors duration-300"
              style={{ color: isLocked ? (isDark ? colors.cyan : colors.blue) : undefined }}
            >
              {themeMode === "auto" ? "◐" : themeMode === "light" ? "☀" : "☾"}
            </span>
            <span className="hidden sm:inline w-8">
              {themeMode}
            </span>
            <span
              className="w-1.5 h-1.5 rounded-full transition-opacity duration-300"
              style={{
                backgroundColor: isDark ? colors.cyan : colors.blue,
                opacity: isLocked ? 1 : 0,
              }}
            />
          </button>

          <button
            onClick={onOpenWaitlist}
            className="px-3 py-1.5 md:px-5 md:py-2.5 rounded-full text-xs md:text-sm font-medium transition-all duration-300"
            style={{
              backgroundColor: isDark
                ? `${colors.magenta}20`
                : `${colors.magenta}15`,
              border: `1px solid ${isDark ? `${colors.magenta}40` : `${colors.magenta}30`}`,
              color: colors.magenta,
            }}
          >
            Join Waitlist
          </button>
        </div>
      </div>
    </nav>
  );
}
